import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Send, ShieldAlert, Flag, Lock, CheckCheck, Check } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  requested: "Requested",
  accepted: "Accepted",
  picked_up: "Picked up",
  in_transit: "In transit",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const timeLabel = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return `${d.toLocaleDateString([], { day: "2-digit", month: "short" })} · ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return ""; }
};

const SafetyNotice = () => (
  <div
    data-testid="chat-safety-notice"
    className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-start gap-2 text-[12px] text-amber-900 leading-relaxed"
  >
    <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-amber-700"/>
    <div>
      <span className="font-medium">Keep all communication and payment on CarryIt.</span>{" "}
      Arranging delivery or payment outside the platform removes CarryIt Protection and may result in account suspension.
    </div>
  </div>
);

const StatusPill = ({ chat_status }) => {
  if (chat_status === "readonly") return (
    <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
      <Lock className="w-3 h-3"/> Read-only
    </span>
  );
  if (chat_status === "closed") return (
    <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-rose-700 bg-rose-50 px-2 py-1 rounded-full">
      <Lock className="w-3 h-3"/> Closed
    </span>
  );
  return (
    <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
      Live
    </span>
  );
};

const Chat = ({ deliveryId }) => {
  const { user } = useAuth();
  const [header, setHeader] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatStatus, setChatStatus] = useState("active");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportMsgId, setReportMsgId] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const scrollerRef = useRef(null);
  const lastAtRef = useRef(null);

  const canWrite = chatStatus === "active";
  const canRead = chatStatus !== "closed" || messages.length > 0;
  const disabledReason = useMemo(() => {
    if (chatStatus === "readonly") return "This conversation is read-only (7 days after delivery).";
    if (chatStatus === "closed") return header?.status === "cancelled"
      ? "This parcel was cancelled — chat is closed."
      : "This conversation is closed.";
    return "";
  }, [chatStatus, header]);

  const loadHeader = async () => {
    try {
      const r = await api.get(`/chat/conversations/${deliveryId}`);
      setHeader(r.data);
      setChatStatus(r.data.chat_status);
    } catch (e) {
      // 404 = conversation doesn't exist yet (parcel not accepted) — hide UI
      if (e?.response?.status !== 404) toast.error("Couldn't load chat header");
      setHeader({ chat_status: "closed" });
      setChatStatus("closed");
    }
  };

  const loadMessages = async () => {
    try {
      const r = await api.get(`/chat/conversations/${deliveryId}/messages`);
      setMessages(r.data.messages || []);
      setChatStatus(r.data.chat_status);
      // Auto-mark read on open
      if ((r.data.messages || []).some((m) => !m.mine && !m.read)) {
        await api.post(`/chat/conversations/${deliveryId}/read`).catch(() => {});
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadHeader();
    loadMessages();
    const iv = setInterval(loadMessages, 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryId]);

  useEffect(() => {
    scrollerRef.current?.scrollTo(0, scrollerRef.current.scrollHeight);
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t || !canWrite) return;
    setBusy(true); setText("");
    try {
      const r = await api.post(`/chat/conversations/${deliveryId}/messages`, { text: t });
      setMessages((m) => [...m, r.data.message]);
      if (r.data.pii_masked) toast.warning(r.data.pii_notice, { duration: 6000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
      setText(t);
    } finally { setBusy(false); }
  };

  const openReport = (msgId = null) => {
    setReportMsgId(msgId);
    setReportReason("");
    setReportOpen(true);
  };
  const submitReport = async () => {
    if (!reportReason.trim()) { toast.error("Please describe what you're reporting."); return; }
    try {
      await api.post(`/chat/conversations/${deliveryId}/report`, {
        message_id: reportMsgId, reason: reportReason.trim(),
      });
      toast.success("Reported. Our team will review it.");
      setReportOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't submit report."); }
  };

  if (!header) return null;
  if (header.chat_status === "closed" && messages.length === 0) {
    // Nothing to show yet — pre-acceptance state (chat only opens after accept).
    return null;
  }

  return (
    <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="chat-panel">
      {/* Fixed header: parcel + route + status */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-400" data-testid="chat-parcel-id">
            Parcel · #{header.parcel_id?.slice(0, 8) || "—"}
          </div>
          <div className="text-sm font-display font-semibold truncate" data-testid="chat-route">
            {header.route?.from || "?"} → {header.route?.to || "?"}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Status · <span className="font-medium text-slate-700">{STATUS_LABEL[header.status] || header.status}</span>
          </div>
        </div>
        <StatusPill chat_status={chatStatus}/>
      </div>

      <SafetyNotice/>

      {/* Message list */}
      <div ref={scrollerRef} className="max-h-80 min-h-[220px] overflow-y-auto px-4 py-3 space-y-2 bg-slate-50" data-testid="chat-scroller">
        {messages.length === 0 && (
          <div className="text-center text-xs text-slate-400 py-8">
            No messages yet · say hi to coordinate the pickup
          </div>
        )}
        {messages.map((m) => {
          const mine = m.mine;
          return (
            <div key={m.id} className={`group flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-slate-900 text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"}`}>
                {!mine && (
                  <div className="text-[10px] text-slate-500 mb-0.5 uppercase tracking-wider flex items-center gap-1.5">
                    <span>{m.role}</span>
                    <span className="text-slate-400">·</span>
                    <span>{m.from_name || m.role}</span>
                  </div>
                )}
                <div className="whitespace-pre-wrap break-words" data-testid={`chat-msg-${m.id}`}>{m.text}</div>
                <div className={`mt-1 flex items-center gap-1 text-[10px] ${mine ? "text-white/60 justify-end" : "text-slate-400"}`}>
                  {m.masked && (
                    <span className="uppercase tracking-wider inline-flex items-center gap-1">
                      <ShieldAlert className={`w-3 h-3 ${mine ? "text-amber-300" : "text-amber-600"}`}/> hidden contact
                    </span>
                  )}
                  <span>{timeLabel(m.created_at)}</span>
                  {mine && (m.read ? <CheckCheck className="w-3 h-3"/> : <Check className="w-3 h-3"/>)}
                  {!mine && (
                    <button
                      onClick={() => openReport(m.id)}
                      className="ml-1 opacity-0 group-hover:opacity-100 transition text-slate-400 hover:text-rose-600"
                      title="Report this message"
                      data-testid={`chat-report-${m.id}`}
                    >
                      <Flag className="w-3 h-3"/>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input area */}
      <div className="px-3 py-3 border-t border-slate-100 flex items-center gap-2">
        <Input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
          placeholder={canWrite ? "Type a message…" : disabledReason}
          className="rounded-full"
          disabled={!canWrite || busy}
        />
        <Button
          data-testid="chat-send"
          onClick={send}
          disabled={busy || !text.trim() || !canWrite}
          size="icon"
          className="rounded-full bg-amber-600 hover:bg-amber-700"
        >
          <Send className="w-4 h-4"/>
        </Button>
      </div>
      <div className="px-4 pb-3 flex items-center justify-between">
        {!canWrite && (
          <div className="text-[11px] text-slate-500 flex items-center gap-1" data-testid="chat-lock-notice">
            <Lock className="w-3 h-3"/> {disabledReason}
          </div>
        )}
        {canRead && (
          <button
            onClick={() => openReport(null)}
            className="text-[11px] text-slate-400 hover:text-rose-600 inline-flex items-center gap-1 ml-auto"
            data-testid="chat-report-conversation"
          >
            <Flag className="w-3 h-3"/> Report conversation
          </button>
        )}
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="chat-report-dialog">
          <DialogHeader>
            <DialogTitle>Report {reportMsgId ? "this message" : "this conversation"}</DialogTitle>
            <DialogDescription>
              Tell us what's happening. Our team reviews every report.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="chat-report-reason"
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            placeholder="e.g., trying to move payment off-app, abusive language, spam"
            rows={4}
            maxLength={500}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)}>Cancel</Button>
            <Button
              onClick={submitReport}
              disabled={!reportReason.trim()}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="chat-report-submit"
            >
              Submit report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Chat;
